/**
 * @file Re-export de break_infinity.js (Decimal) + helpers de conversion/serialisation pour grands nombres.
 */
import Decimal from 'break_infinity.js';
import type { DecimalSource } from 'break_infinity.js';

export { Decimal };
export type { DecimalSource };

/** Grand nombre serialise (mantisse/exposant en chaine, ex. "1.5e30"). Forme stockee dans les sauvegardes. */
export type BigStr = string;

/** Raccourci de construction. */
export const D = (v: DecimalSource): Decimal => Decimal.fromValue(v);

export const ZERO: Decimal = new Decimal(0);
export const ONE: Decimal = new Decimal(1);

export const toBigStr = (d: Decimal): BigStr => d.toString();

export const fromBigStr = (s: BigStr | undefined | null, fallback: DecimalSource = 0): Decimal => {
  if (s === undefined || s === null || s === '') return Decimal.fromValue(fallback);
  const d = Decimal.fromString(s);
  // Une chaine corrompue donne NaN : on retombe sur le fallback plutot que de propager NaN dans l'economie.
  return Number.isNaN(d.m) ? Decimal.fromValue(fallback) : d;
};

export const dMax = (a: DecimalSource, b: DecimalSource): Decimal => Decimal.max(a, b);
export const dMin = (a: DecimalSource, b: DecimalSource): Decimal => Decimal.min(a, b);

/** a^n pour n flottant, sans overflow (1.08^10000 depasse Number.MAX_VALUE). */
export const dPow = (base: number, exp: number): Decimal => Decimal.pow(base, exp);

/** Convertit en nombre JS en saturant (pour UI/jauges uniquement, jamais pour l'economie). */
export const toNumberSafe = (d: Decimal): number => {
  const n = d.toNumber();
  return Number.isFinite(n) ? n : n > 0 ? Number.MAX_VALUE : -Number.MAX_VALUE;
};
